/**
 * Recursos dos Disparos em Massa que o ponteiro de uma novidade sabe apontar. Ver `spotlightTargets.js`.
 *
 * A tela guarda tudo em estado do React: a lista, o assistente de 4 passos e o detalhe de um disparo
 * trocam de lugar sem mudar a URL. O destino de qualquer um é sempre a lista, então tudo que mora no
 * assistente ou no detalhe tem um roteiro (`steps`) que parte do botão "Novo Disparo" ou de uma linha
 * da lista. Cada passo do assistente só existe enquanto a pessoa está nele, e voltar um passo
 * desmonta o anterior: o ponteiro de um recurso do passo 3 espera até a pessoa chegar lá.
 */

const NOVO = "broadcasts.new-broadcast";
const PROXIMO = "broadcasts.next-button";

const LISTA = "Lista de disparos";
const ASSISTENTE = "Assistente: visão geral";
const AUDIENCIA = "Passo 1: Audiência";
const TEMPLATE = "Passo 2: Template e canal";
const CONFIG = "Passo 3: Configuração";
const PREVIA = "Passo 4: Prévia e envio";
const DETALHE = "Acompanhamento de um disparo";

const abrirAssistente = { key: NOVO, text: "Clique em Novo Disparo" };
const avancar = (ate) => ({ key: PROXIMO, text: `Avance com Próximo até a etapa ${ate}` });

/** Roteiro até um recurso de um passo do assistente: abrir, avançar até o passo. */
const stepsDoPasso = (passo, ...extras) => [
  { ...abrirAssistente, text: `${abrirAssistente.text}.` },
  ...(passo ? [{ ...avancar(passo), text: `${avancar(passo).text}.` }] : []),
  ...extras,
];
const stepsDaAudiencia = (...extras) => stepsDoPasso(null, ...extras);
const stepsDeUmDisparo = [{ key: "broadcasts.campaign-list", text: "Clique em um disparo da lista para abrir o acompanhamento." }];

export const BROADCASTS_TARGETS = [
  // ── Lista de disparos ───────────────────────────────────────────────────
  {
    key: "broadcasts.new-broadcast",
    navKey: "broadcasts",
    group: LISTA,
    label: "Botão Novo Disparo",
    path: "/broadcasts",
    requires: "page",
    hint: "Começa uma campanha de mensagens em massa em 4 passos: audiência, template, configuração e prévia.",
  },
  {
    key: "broadcasts.status-filter",
    navKey: "broadcasts",
    group: LISTA,
    label: "Filtro de situação dos disparos",
    path: "/broadcasts",
    requires: "page",
    hint: "Filtra a lista por rascunho, agendado, enviando, pausado, concluído, falha ou cancelado.",
  },
  {
    key: "broadcasts.list-refresh",
    navKey: "broadcasts",
    group: LISTA,
    label: "Atualizar a lista",
    path: "/broadcasts",
    requires: "page",
    hint: "O ícone de setas recarrega a lista e os números do topo.",
  },
  {
    key: "broadcasts.stats-bar",
    navKey: "broadcasts",
    group: LISTA,
    label: "Resumo de números (total, enviando, agendados, concluídos, taxa de entrega)",
    path: "/broadcasts",
    requires: "data",
    hint: "A faixa de números do topo da lista, com a taxa de entrega dos últimos 30 dias. Some se o servidor não responder.",
  },
  {
    key: "broadcasts.campaign-list",
    navKey: "broadcasts",
    group: LISTA,
    label: "Histórico de disparos",
    path: "/broadcasts",
    requires: "data",
    hint: "A lista de campanhas já criadas; cada linha abre o acompanhamento. Só existe depois do primeiro disparo.",
  },

  // ── Assistente ──────────────────────────────────────────────────────────
  {
    key: "broadcasts.wizard-stepper",
    navKey: "broadcasts",
    group: ASSISTENTE,
    label: "Barra dos 4 passos do assistente",
    path: "/broadcasts",
    requires: "panel",
    hint: "Mostra em que passo a pessoa está e deixa voltar a um passo já concluído. Aparece quando o assistente está aberto.",
    steps: stepsDoPasso(null),
  },
  {
    key: "broadcasts.next-button",
    navKey: "broadcasts",
    group: ASSISTENTE,
    label: "Botão Próximo",
    path: "/broadcasts",
    requires: "panel",
    hint: "Avança de passo quando o passo atual está completo. Do passo 4 em diante vira o botão de enviar.",
    steps: stepsDoPasso(null),
  },

  // ── Passo 1: Audiência ──────────────────────────────────────────────────
  {
    key: "broadcasts.audience-type",
    navKey: "broadcasts",
    group: AUDIENCIA,
    label: "Tipo de audiência",
    path: "/broadcasts",
    requires: "panel",
    hint: "Escolhe quem recebe: por tags, audiência salva, planilha CSV ou Excel, todos os contatos ou seleção manual.",
    steps: stepsDaAudiencia(),
  },
  {
    key: "broadcasts.audience-tags",
    navKey: "broadcasts",
    group: AUDIENCIA,
    label: "Incluir e excluir contatos por tags",
    path: "/broadcasts",
    requires: "panel",
    hint: "O tipo padrão: marque as tags de quem deve receber e as tags de quem deve ficar de fora.",
    steps: stepsDaAudiencia({ key: "broadcasts.audience-type", text: "Em Tipo de Audiência, escolha Filtrar por Tags." }),
  },
  {
    key: "broadcasts.audience-saved",
    navKey: "broadcasts",
    group: AUDIENCIA,
    label: "Escolher uma audiência salva",
    path: "/broadcasts",
    requires: "panel",
    hint: "Reaproveita uma lista de contatos já montada. Só aparece no tipo Audiência Salva.",
    steps: stepsDaAudiencia({ key: "broadcasts.audience-type", text: "Em Tipo de Audiência, escolha Audiência Salva." }),
  },
  {
    key: "broadcasts.audience-csv-import",
    navKey: "broadcasts",
    group: AUDIENCIA,
    label: "Importar planilha (CSV ou Excel)",
    path: "/broadcasts",
    requires: "panel",
    hint: "A área de envio da planilha: a coluna de telefone é obrigatória e as outras colunas viram variáveis da mensagem. Só no tipo Importar CSV / Excel.",
    steps: stepsDaAudiencia({ key: "broadcasts.audience-type", text: "Em Tipo de Audiência, escolha Importar CSV / Excel." }),
  },
  {
    key: "broadcasts.audience-csv-example",
    navKey: "broadcasts",
    group: AUDIENCIA,
    label: "Baixar a planilha modelo",
    path: "/broadcasts",
    requires: "panel",
    hint: "O link Baixar modelo entrega um CSV pronto para preencher. Só no tipo Importar CSV / Excel.",
    steps: stepsDaAudiencia({ key: "broadcasts.audience-type", text: "Em Tipo de Audiência, escolha Importar CSV / Excel." }),
  },
  {
    key: "broadcasts.audience-last-contact",
    navKey: "broadcasts",
    group: AUDIENCIA,
    label: "Filtro por último contato",
    path: "/broadcasts",
    requires: "panel",
    hint: "Limita a audiência a quem não fala com a empresa há X dias, ou falou nos últimos X dias. Nos tipos Tags e Todos os Contatos.",
    steps: stepsDaAudiencia({ key: "broadcasts.audience-type", text: "Em Tipo de Audiência, escolha Filtrar por Tags ou Todos os Contatos." }),
  },
  {
    key: "broadcasts.audience-manual",
    navKey: "broadcasts",
    group: AUDIENCIA,
    label: "Seleção manual de contatos",
    path: "/broadcasts",
    requires: "panel",
    hint: "O aviso do modo manual: marque contato por contato na lista da direita. Só no tipo Seleção Manual.",
    steps: stepsDaAudiencia({ key: "broadcasts.audience-type", text: "Em Tipo de Audiência, escolha Seleção Manual." }),
  },
  {
    key: "broadcasts.audience-counter",
    navKey: "broadcasts",
    group: AUDIENCIA,
    label: "Contador de contatos da audiência",
    path: "/broadcasts",
    requires: "panel",
    hint: "Quantos contatos com WhatsApp ativo vão receber. O botão Próximo só libera com 1 ou mais.",
    steps: stepsDaAudiencia(),
  },
  {
    key: "broadcasts.audience-search",
    navKey: "broadcasts",
    group: AUDIENCIA,
    label: "Buscar contato na audiência",
    path: "/broadcasts",
    requires: "panel",
    hint: "O campo de busca da lista de contatos da audiência.",
    steps: stepsDaAudiencia(),
  },
  {
    key: "broadcasts.audience-preview",
    navKey: "broadcasts",
    group: AUDIENCIA,
    label: "Lista de contatos da audiência",
    path: "/broadcasts",
    requires: "panel",
    hint: "A prévia de quem recebe: clicar num contato o tira do envio, e no modo manual marca quem entra.",
    steps: stepsDaAudiencia(),
  },

  // ── Passo 2: Template e canal ───────────────────────────────────────────
  {
    key: "broadcasts.channel-picker",
    navKey: "broadcasts",
    group: TEMPLATE,
    label: "Canal de envio",
    path: "/broadcasts",
    requires: "panel",
    hint: "Escolhe de qual número de WhatsApp a campanha sai; os templates dependem do canal. Com um só número ele já vem escolhido.",
    steps: stepsDoPasso("Template"),
  },
  {
    key: "broadcasts.template-filters",
    navKey: "broadcasts",
    group: TEMPLATE,
    label: "Buscar template e filtrar por categoria",
    path: "/broadcasts",
    requires: "panel",
    hint: "Acha o template pelo nome ou pela categoria (marketing, utilidade).",
    steps: stepsDoPasso("Template"),
  },
  {
    key: "broadcasts.template-list",
    navKey: "broadcasts",
    group: TEMPLATE,
    label: "Lista de templates aprovados",
    path: "/broadcasts",
    requires: "panel",
    hint: "Só os templates aprovados pela Meta aparecem, depois de escolher o canal. Clicar em um o seleciona.",
    steps: stepsDoPasso("Template"),
  },
  {
    key: "broadcasts.template-variables",
    navKey: "broadcasts",
    group: TEMPLATE,
    label: "Campos variáveis do template",
    path: "/broadcasts",
    requires: "panel",
    hint: "Cada variável do template recebe um texto fixo, o nome do contato ou uma coluna da planilha. Só em template que tem variáveis ou mídia no cabeçalho.",
    steps: stepsDoPasso("Template", { key: "broadcasts.template-list", text: "Clique em um template da lista." }),
  },
  {
    key: "broadcasts.template-media-header",
    navKey: "broadcasts",
    group: TEMPLATE,
    label: "Mídia do cabeçalho do template",
    path: "/broadcasts",
    requires: "panel",
    hint: "Enviar o arquivo ou colar uma URL pública para o cabeçalho de imagem, vídeo ou documento. Só em template que tem esse tipo de cabeçalho.",
    steps: stepsDoPasso("Template", { key: "broadcasts.template-list", text: "Clique em um template com imagem, vídeo ou documento no cabeçalho." }),
  },
  {
    key: "broadcasts.template-preview",
    navKey: "broadcasts",
    group: TEMPLATE,
    label: "Prévia da mensagem no celular (passo Template)",
    path: "/broadcasts",
    requires: "panel",
    hint: "Mostra como o template aparece no WhatsApp do cliente. Só depois de escolher um template.",
    steps: stepsDoPasso("Template", { key: "broadcasts.template-list", text: "Clique em um template da lista." }),
  },

  // ── Passo 3: Configuração ───────────────────────────────────────────────
  {
    key: "broadcasts.sending-speed",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Velocidade de envio",
    path: "/broadcasts",
    requires: "panel",
    hint: "Lento, normal ou rápido. Velocidades altas enviam mais depressa, mas a Meta pode limitar.",
    steps: stepsDoPasso("Configuração"),
  },
  {
    key: "broadcasts.schedule",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Agendamento do disparo",
    path: "/broadcasts",
    requires: "panel",
    hint: "A chave que programa o envio para uma data e hora em vez de enviar agora.",
    steps: stepsDoPasso("Configuração"),
  },
  {
    key: "broadcasts.recurrence",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Repetir envio (recorrência)",
    path: "/broadcasts",
    requires: "panel",
    hint: "Repete o disparo automaticamente (por exemplo, toda segunda às 9h) até um limite. Só existe com o agendamento ligado.",
    steps: stepsDoPasso("Configuração", { key: "broadcasts.schedule", text: "Ligue a chave de Agendamento." }),
  },
  {
    key: "broadcasts.reply-flow",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Fluxo de resposta",
    path: "/broadcasts",
    requires: "panel",
    hint: "Quem responder ao disparo em até 72 horas cai direto no fluxo publicado escolhido.",
    steps: stepsDoPasso("Configuração"),
  },
  {
    key: "broadcasts.crm-deal",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Negócio no CRM",
    path: "/broadcasts",
    requires: "panel",
    feature: "crm.enabled",
    permission: "crm.read",
    hint: "A chave que cria um negócio no funil para cada contato do disparo. Só para quem tem o CRM no plano e lê o CRM.",
    steps: stepsDoPasso("Configuração"),
  },
  {
    key: "broadcasts.crm-deal-trigger",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Quando criar o negócio",
    path: "/broadcasts",
    requires: "panel",
    feature: "crm.enabled",
    permission: "crm.read",
    hint: "Criar o negócio só de quem responde (recomendado) ou de todo mundo que recebe. Só com Negócio no CRM ligado.",
    steps: stepsDoPasso("Configuração", { key: "broadcasts.crm-deal", text: "Ligue a chave Negócio no CRM." }),
  },
  {
    key: "broadcasts.crm-deal-pipeline",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Funil e etapa do negócio",
    path: "/broadcasts",
    requires: "panel",
    feature: "crm.enabled",
    permission: "crm.read",
    hint: "Em qual funil e etapa os negócios nascem; o Próximo só libera com os dois escolhidos. Só com Negócio no CRM ligado.",
    steps: stepsDoPasso("Configuração", { key: "broadcasts.crm-deal", text: "Ligue a chave Negócio no CRM." }),
  },
  {
    key: "broadcasts.crm-deal-title",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Título do negócio",
    path: "/broadcasts",
    requires: "panel",
    feature: "crm.enabled",
    permission: "crm.read",
    hint: "O título aceita variáveis, como o nome do contato, e mostra um exemplo. Só com Negócio no CRM ligado.",
    steps: stepsDoPasso("Configuração", { key: "broadcasts.crm-deal", text: "Ligue a chave Negócio no CRM." }),
  },
  {
    key: "broadcasts.crm-deal-value-owner",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Valor e responsável do negócio",
    path: "/broadcasts",
    requires: "panel",
    feature: "crm.enabled",
    permission: "crm.read",
    hint: "O valor opcional e quem fica com o negócio. Só com Negócio no CRM ligado.",
    steps: stepsDoPasso("Configuração", { key: "broadcasts.crm-deal", text: "Ligue a chave Negócio no CRM." }),
  },
  {
    key: "broadcasts.crm-deal-skip-open",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Pular quem já tem negócio aberto",
    path: "/broadcasts",
    requires: "panel",
    feature: "crm.enabled",
    permission: "crm.read",
    hint: "Evita negócios repetidos para o mesmo contato no funil. Só com Negócio no CRM ligado.",
    steps: stepsDoPasso("Configuração", { key: "broadcasts.crm-deal", text: "Ligue a chave Negócio no CRM." }),
  },
  {
    key: "broadcasts.crm-deal-suppress-automations",
    navKey: "broadcasts",
    group: CONFIG,
    label: "Não disparar as automações do CRM",
    path: "/broadcasts",
    requires: "panel",
    feature: "crm.enabled",
    permission: "crm.read",
    hint: "Cria os negócios sem acionar as automações de negócio criado; recomendado para listas grandes. Só com Negócio no CRM ligado.",
    steps: stepsDoPasso("Configuração", { key: "broadcasts.crm-deal", text: "Ligue a chave Negócio no CRM." }),
  },

  // ── Passo 4: Prévia e envio ─────────────────────────────────────────────
  {
    key: "broadcasts.broadcast-name",
    navKey: "broadcasts",
    group: PREVIA,
    label: "Nome do disparo",
    path: "/broadcasts",
    requires: "panel",
    hint: "O nome com que a campanha aparece no histórico; é obrigatório para enviar.",
    steps: stepsDoPasso("Prévia"),
  },
  {
    key: "broadcasts.review-summary",
    navKey: "broadcasts",
    group: PREVIA,
    label: "Resumo do disparo",
    path: "/broadcasts",
    requires: "panel",
    hint: "Audiência, template, canal e tempo estimado, para revisar antes de enviar.",
    steps: stepsDoPasso("Prévia"),
  },
  {
    key: "broadcasts.review-preview",
    navKey: "broadcasts",
    group: PREVIA,
    label: "Prévia final com os dados do primeiro contato",
    path: "/broadcasts",
    requires: "panel",
    hint: "A mensagem exatamente como o primeiro contato da lista vai recebê-la.",
    steps: stepsDoPasso("Prévia"),
  },
  {
    key: "broadcasts.submit-broadcast",
    navKey: "broadcasts",
    group: PREVIA,
    label: "Botão Enviar ou Agendar Disparo",
    path: "/broadcasts",
    requires: "panel",
    hint: "Dispara agora ou agenda, conforme a configuração. Depois a pessoa acompanha o progresso em tempo real.",
    steps: stepsDoPasso("Prévia"),
  },

  // ── Acompanhamento de um disparo ────────────────────────────────────────
  {
    key: "broadcasts.detail-progress",
    navKey: "broadcasts",
    group: DETALHE,
    label: "Progresso e métricas do disparo",
    path: "/broadcasts",
    requires: "panel",
    hint: "A barra de progresso e os números de enviados, entregues, lidos e falhas, ao vivo. Aparece ao abrir um disparo da lista.",
    steps: stepsDeUmDisparo,
  },
  {
    key: "broadcasts.detail-export",
    navKey: "broadcasts",
    group: DETALHE,
    label: "Exportar o relatório do disparo",
    path: "/broadcasts",
    requires: "panel",
    hint: "Baixa os destinatários com status, erro e horários em CSV ou Excel. Aparece ao abrir um disparo da lista.",
    steps: stepsDeUmDisparo,
  },
  {
    key: "broadcasts.detail-pause-resume",
    navKey: "broadcasts",
    group: DETALHE,
    label: "Pausar e retomar o disparo",
    path: "/broadcasts",
    requires: "panel",
    hint: "Pausar aparece com o disparo enviando e Retomar com ele pausado (também pausa sozinho se muitos falharem).",
    steps: stepsDeUmDisparo,
  },
  {
    key: "broadcasts.detail-cancel",
    navKey: "broadcasts",
    group: DETALHE,
    label: "Cancelar o disparo",
    path: "/broadcasts",
    requires: "panel",
    hint: "Cancela um disparo agendado, enviando ou pausado; o que já foi enfileirado segue.",
    steps: stepsDeUmDisparo,
  },
  {
    key: "broadcasts.detail-duplicate",
    navKey: "broadcasts",
    group: DETALHE,
    label: "Duplicar o disparo",
    path: "/broadcasts",
    requires: "panel",
    hint: "Cria uma cópia de um disparo concluído, com falha ou cancelado.",
    steps: stepsDeUmDisparo,
  },
  {
    key: "broadcasts.detail-delete",
    navKey: "broadcasts",
    group: DETALHE,
    label: "Excluir o disparo",
    path: "/broadcasts",
    requires: "panel",
    hint: "Remove o histórico de um disparo concluído, com falha ou cancelado.",
    steps: stepsDeUmDisparo,
  },
  {
    key: "broadcasts.detail-recipients",
    navKey: "broadcasts",
    group: DETALHE,
    label: "Lista de destinatários (busca e filtro de situação)",
    path: "/broadcasts",
    requires: "panel",
    hint: "Status, erro e tentativas de cada contato, com busca e filtro para achar as falhas. Aparece ao abrir um disparo da lista.",
    steps: stepsDeUmDisparo,
  },
];
